// === Admin: Distributors ===
// Full CRUD page — list, add, edit, soft-delete.
// Ported from Click2Gro PHP (DistributorsController.php / distributors table)
// Columns: company, street, suburb, postcode, state, phone_no + Actions
// Form fields: all available fields from the PHP model

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Divider as _Divider,
  Tabs,
  Tab,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  OpenInNew as WebIcon,
  LinkOutlined as MatchIcon,
  Sync as SyncIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import Autocomplete from '@mui/material/Autocomplete';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';
import {
  CatalogueCountrySwitch,
  catalogueCountryName,
  CatalogueUkAddressFields,
  UK_COUNTRY_NAME,
  useCatalogueCountry,
  toCatalogueCountry,
  type CatalogueCountry,
} from '../components/CatalogueCountrySwitch';
import { useCountries } from '../lib/countries';

// ---------------------------------------------------------------------------
// Lookup types for State / Suburb dropdowns
// ---------------------------------------------------------------------------

interface StateOption  { readonly state_id: number; readonly state: string; }
interface SuburbOption { readonly id: number; readonly suburb: string; readonly postcode: string; readonly country: string; }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DistributorRow {
  readonly id:                number;
  readonly company:           string | null;
  readonly street:            string | null;
  readonly suburb:            string | null;
  readonly suburb_name:       string | null;
  readonly state_name:        string | null;
  readonly postcode:          string | null;
  readonly state:             string | null;
  readonly country:           string | null;
  readonly phone_no:          string | null;
  readonly mobile_number:     string | null;
  readonly abn:               string | null;
  readonly division:          string | null;
  readonly title:             string | null;
  readonly first_name:        string | null;
  readonly last_name:         string | null;
  readonly email_address:     string | null;
  readonly web_link:          string | null;
  readonly client_id:         number | null;
  readonly invoice_prefix:    string | null;
  readonly country_code?:     string | null;
  readonly created_at:        string | null;
  readonly updated_at:        string | null;
  readonly created_by:        string | null;
  readonly updated_by:        string | null;
}

interface FormState {
  company:        string;
  street:         string;
  suburb:         string;
  postcode:       string;
  state:          string;
  country:        string;
  phone_no:       string;
  mobile_number:  string;
  abn:            string;
  division:       string;
  title:          string;
  first_name:     string;
  last_name:      string;
  email_address:  string;
  web_link:       string;
  client_id:      string;
  invoice_prefix: string;
  country_code:   CatalogueCountry;
}

const DEFAULT_FORM: FormState = {
  company:        '',
  street:         '',
  suburb:         '',
  postcode:       '',
  state:          '',
  country:        '',
  phone_no:       '',
  mobile_number:  '',
  abn:            '',
  division:       '',
  title:          '',
  first_name:     '',
  last_name:      '',
  email_address:  '',
  web_link:       '',
  client_id:      '',
  invoice_prefix: '',
  country_code:   'AU',
};

// ---------------------------------------------------------------------------
// Store / Product types
// ---------------------------------------------------------------------------

interface StoreRow {
  readonly id:             number;
  readonly distributor_id: number;
  readonly store_name:     string | null;
  readonly store_location: string | null;
  readonly phone_number:   string | null;
  readonly email_address:  string | null;
  readonly full_address:   string | null;
  readonly website:        string | null;
  readonly state:          string | null;
  readonly suburb:         string | null;
  readonly postcode:       string | null;
  readonly country:        string | null;
  readonly suburb_name:    string | null;
  readonly state_name:     string | null;
}

interface StoreForm {
  store_name:     string;
  store_location: string;
  phone_number:   string;
  email_address:  string;
  full_address:   string;
  website:        string;
  state:          string;
  suburb:         string;
  postcode:       string;
  country:        string;
}

const EMPTY_STORE_FORM: StoreForm = {
  store_name: '', store_location: '', phone_number: '', email_address: '',
  full_address: '', website: '', state: '', suburb: '', postcode: '', country: '',
};

interface ProductRow {
  readonly id:             number;
  readonly distributor_id: number;
  readonly product_name:   string | null;
  readonly product_code:   string | null;
  readonly unit:           string | null;
  readonly chemical_id:    number | null;
  readonly fertilizer_id:  number | null;
  readonly chemical_name:   string | null;
  readonly fertilizer_name: string | null;
}

interface ProductForm {
  product_name: string;
  product_code: string;
  unit:         string;
}

const EMPTY_PRODUCT_FORM: ProductForm = { product_name: '', product_code: '', unit: '' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(d);
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function DistributorsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const [country, setCountry] = useCatalogueCountry();
  const countries = useCountries();

  // ---- List state ----
  const [rows, setRows] = useState<readonly DistributorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [auditInfo, setAuditInfo] = useState<{
    created_at: string | null; updated_at: string | null;
    created_by: string | null; updated_by: string | null;
  } | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ---- State / Suburb cascading dropdowns ----
  const [stateOptions, setStateOptions]   = useState<readonly StateOption[]>([]);
  const [suburbOptions, setSuburbOptions] = useState<readonly SuburbOption[]>([]);
  const [loadingSuburbs, setLoadingSuburbs] = useState(false);
  const suburbDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId]       = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting]       = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{
    open: boolean; message: string; severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---- Edit dialog tabs ----
  const [activeTab, setActiveTab] = useState(0);

  // ---- Stores ----
  const [stores, setStores] = useState<readonly StoreRow[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesError, setStoresError] = useState<string | null>(null);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [editingStoreId, setEditingStoreId] = useState<number | null>(null);
  const [storeForm, setStoreForm] = useState<StoreForm>(EMPTY_STORE_FORM);
  const [storeFormError, setStoreFormError] = useState<string | null>(null);
  const [storeSaving, setStoreSaving] = useState(false);
  const [storeSuburbOptions, setStoreSuburbOptions] = useState<readonly SuburbOption[]>([]);
  const [storeLoadingSuburbs, setStoreLoadingSuburbs] = useState(false);
  const storeSuburbDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteStoreId, setDeleteStoreId] = useState<number | null>(null);
  const [deleteStoreLabel, setDeleteStoreLabel] = useState('');
  const [deletingStore, setDeletingStore] = useState(false);

  // ---- Products ----
  const [products, setProducts] = useState<readonly ProductRow[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [addProductForm, setAddProductForm] = useState<ProductForm>(EMPTY_PRODUCT_FORM);
  const [addProductError, setAddProductError] = useState<string | null>(null);
  const [addProductSaving, setAddProductSaving] = useState(false);
  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [editProductForm, setEditProductForm] = useState<ProductForm>(EMPTY_PRODUCT_FORM);
  const [editProductError, setEditProductError] = useState<string | null>(null);
  const [editProductSaving, setEditProductSaving] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState<number | null>(null);
  const [deleteProductLabel, setDeleteProductLabel] = useState('');
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [matching, setMatching] = useState(false);
  const [productsPage, setProductsPage] = useState(0);
  const [productsRpp, setProductsRpp] = useState(25);

  // CSV Import state
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvMapping, setCsvMapping] = useState<Record<string, string>>({});
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/distributors`);
      url.searchParams.set('page',  String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      url.searchParams.set('country', country);
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);
      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: DistributorRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch, country]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // Load states when dialog opens
  useEffect(() => {
    if (!dialogOpen || !token) return;
    void (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/v1/admin/postcodes/states`, {
          headers: authHeaders(token),
        });
        if (res.ok) {
          const json = (await res.json()) as { data: StateOption[] };
          setStateOptions(json.data);
        }
      } catch { /* silent */ }
    })();
  }, [dialogOpen, token]);

  // Search suburbs server-side as the user types (debounced 300ms, min 2 chars)
  const handleSuburbInputChange = useCallback((_e: React.SyntheticEvent, inputValue: string) => {
    if (suburbDebounceRef.current) clearTimeout(suburbDebounceRef.current);
    if (!form.state || inputValue.length < 2) { setSuburbOptions([]); return; }
    setLoadingSuburbs(true);
    suburbDebounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(
            `${getApiUrl()}/api/v1/admin/postcodes/suburbs-by-state?state_id=${form.state}&search=${encodeURIComponent(inputValue)}`,
            { headers: authHeaders(token) },
          );
          if (res.ok) {
            const json = (await res.json()) as { data: SuburbOption[] };
            setSuburbOptions(json.data);
          }
        } catch { /* silent */ }
        finally { setLoadingSuburbs(false); }
      })();
    }, 300);
  }, [form.state, token]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit / close dialog
  // ---------------------------------------------------------------------------
  const closeDialog = () => {
    setDialogOpen(false);
    setActiveTab(0);
    setAddProductOpen(false);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...DEFAULT_FORM, country_code: country });
    setSuburbOptions([]);
    setFormError(null);
    setAuditInfo(null);
    setActiveTab(0);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    setActiveTab(0);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/distributors/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: DistributorRow };
      const r = json.data;
      setForm({
        company:        r.company        ?? '',
        street:         r.street         ?? '',
        suburb:         r.suburb         ?? '',
        postcode:       r.postcode       ?? '',
        state:          r.state          ?? '',
        country:        r.country        ?? '',
        phone_no:       r.phone_no       ?? '',
        mobile_number:  r.mobile_number  ?? '',
        abn:            r.abn            ?? '',
        division:       r.division       ?? '',
        title:          r.title          ?? '',
        first_name:     r.first_name     ?? '',
        last_name:      r.last_name      ?? '',
        email_address:  r.email_address  ?? '',
        web_link:       r.web_link       ?? '',
        client_id:      r.client_id != null ? String(r.client_id) : '',
        invoice_prefix: r.invoice_prefix ?? '',
        country_code:   toCatalogueCountry(r.country_code ?? country),
      });
      if (r.suburb && r.suburb_name) {
        setSuburbOptions([{ id: Number(r.suburb), suburb: r.suburb_name, postcode: r.postcode ?? '', country: r.country ?? '' }]);
      } else {
        setSuburbOptions([]);
      }
      setAuditInfo({
        created_at: r.created_at ?? null,
        updated_at: r.updated_at ?? null,
        created_by: r.created_by ?? null,
        updated_by: r.updated_by ?? null,
      });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };



  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const trimmedCompany = form.company.trim();
    if (!trimmedCompany)             { setFormError('Company name is required.');                    return; }
    if (trimmedCompany.length > 255) { setFormError('Company must be 255 characters or fewer.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/distributors/${editingId}`
        : `${getApiUrl()}/api/v1/admin/distributors`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          company:        trimmedCompany,
          client_id:      form.client_id !== '' ? parseInt(form.client_id, 10) : null,
          invoice_prefix: form.invoice_prefix || null,
          street:         form.street         || null,
          suburb:         form.suburb         || null,
          postcode:       form.postcode        || null,
          state:          form.state          || null,
          country:        form.country_code === 'GB' ? UK_COUNTRY_NAME : (form.country || null),
          phone_no:       form.phone_no        || null,
          mobile_number:  form.mobile_number   || null,
          abn:            form.abn             || null,
          division:       form.division        || null,
          title:          form.title           || null,
          first_name:     form.first_name      || null,
          last_name:      form.last_name       || null,
          email_address:  form.email_address   || null,
          web_link:       form.web_link        || null,
          country_code:   form.country_code,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      closeDialog();
      showSnack(editingId ? 'Record updated.' : 'Record added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/distributors/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Record deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Stores CRUD
  // ---------------------------------------------------------------------------
  const fetchStores = useCallback(async () => {
    if (!token || editingId === null) return;
    setStoresLoading(true);
    setStoresError(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/v1/admin/distributor-stores?distributor_id=${editingId}`,
        { headers: authHeaders(token) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: StoreRow[] };
      setStores(json.data);
    } catch (e) {
      setStoresError(e instanceof Error ? e.message : 'Failed to load stores');
    } finally {
      setStoresLoading(false);
    }
  }, [token, editingId]);

  const handleStoreSuburbInputChange = useCallback((_e: React.SyntheticEvent, inputValue: string) => {
    if (storeSuburbDebounceRef.current) clearTimeout(storeSuburbDebounceRef.current);
    if (!storeForm.state || inputValue.length < 2) { setStoreSuburbOptions([]); return; }
    setStoreLoadingSuburbs(true);
    storeSuburbDebounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(
            `${getApiUrl()}/api/v1/admin/postcodes/suburbs-by-state?state_id=${storeForm.state}&search=${encodeURIComponent(inputValue)}`,
            { headers: authHeaders(token) },
          );
          if (res.ok) {
            const json = (await res.json()) as { data: SuburbOption[] };
            setStoreSuburbOptions(json.data);
          }
        } catch { /* silent */ }
        finally { setStoreLoadingSuburbs(false); }
      })();
    }, 300);
  }, [storeForm.state, token]);

  const openAddStore = () => {
    setEditingStoreId(null);
    setStoreForm(EMPTY_STORE_FORM);
    setStoreSuburbOptions([]);
    setStoreFormError(null);
    setStoreDialogOpen(true);
  };

  const openEditStore = (s: StoreRow) => {
    setEditingStoreId(s.id);
    setStoreForm({
      store_name:     s.store_name     ?? '',
      store_location: s.store_location ?? '',
      phone_number:   s.phone_number   ?? '',
      email_address:  s.email_address  ?? '',
      full_address:   s.full_address   ?? '',
      website:        s.website        ?? '',
      state:          s.state          ?? '',
      suburb:         s.suburb         ?? '',
      postcode:       s.postcode       ?? '',
      country:        s.country        ?? '',
    });
    if (s.suburb && s.suburb_name) {
      setStoreSuburbOptions([{ id: Number(s.suburb), suburb: s.suburb_name, postcode: s.postcode ?? '', country: s.country ?? '' }]);
    } else {
      setStoreSuburbOptions([]);
    }
    setStoreFormError(null);
    setStoreDialogOpen(true);
  };

  const handleStoreSave = async () => {
    if (!storeForm.store_name.trim()) { setStoreFormError('Store name is required.'); return; }
    setStoreSaving(true);
    setStoreFormError(null);
    try {
      const url = editingStoreId
        ? `${getApiUrl()}/api/v1/admin/distributor-stores/${editingStoreId}`
        : `${getApiUrl()}/api/v1/admin/distributor-stores`;
      const res = await fetch(url, {
        method: editingStoreId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          distributor_id: editingId,
          store_name:     storeForm.store_name.trim(),
          store_location: storeForm.store_location || null,
          phone_number:   storeForm.phone_number   || null,
          email_address:  storeForm.email_address  || null,
          full_address:   storeForm.full_address   || null,
          website:        storeForm.website        || null,
          state:          storeForm.state          || null,
          suburb:         storeForm.suburb         || null,
          postcode:       storeForm.postcode        || null,
          country:        form.country_code === 'GB' ? UK_COUNTRY_NAME : (storeForm.country || null),
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setStoreDialogOpen(false);
      showSnack(editingStoreId ? 'Store updated.' : 'Store added.');
      void fetchStores();
    } catch (e) {
      setStoreFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setStoreSaving(false);
    }
  };

  const handleStoreDelete = async () => {
    if (!deleteStoreId || !token) return;
    setDeletingStore(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/v1/admin/distributor-stores/${deleteStoreId}`,
        { method: 'DELETE', headers: authHeaders(token) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteStoreId(null);
      showSnack('Store deleted.');
      void fetchStores();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeletingStore(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Products CRUD
  // ---------------------------------------------------------------------------
  const fetchProducts = useCallback(async () => {
    if (!token || editingId === null) return;
    setProductsLoading(true);
    setProductsError(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/v1/admin/distributor-products?distributor_id=${editingId}`,
        { headers: authHeaders(token) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: ProductRow[] };
      setProducts(json.data);
      setProductsPage(0);
    } catch (e) {
      setProductsError(e instanceof Error ? e.message : 'Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  }, [token, editingId]);

  // Lazy-load stores / products when tab is switched in edit mode
  useEffect(() => {
    if (activeTab === 1 && editingId !== null) void fetchStores();
  }, [activeTab, editingId, fetchStores]);

  useEffect(() => {
    if (activeTab === 2 && editingId !== null) void fetchProducts();
  }, [activeTab, editingId, fetchProducts]);

  const handleAddProduct = async () => {
    if (!addProductForm.product_name.trim()) { setAddProductError('Product name is required.'); return; }
    setAddProductSaving(true);
    setAddProductError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/distributor-products`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          distributor_id: editingId,
          product_name:   addProductForm.product_name.trim(),
          product_code:   addProductForm.product_code || null,
          unit:           addProductForm.unit         || null,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setAddProductForm(EMPTY_PRODUCT_FORM);
      setAddProductOpen(false);
      showSnack('Product added.');
      void fetchProducts();
    } catch (e) {
      setAddProductError(e instanceof Error ? e.message : 'Add failed');
    } finally {
      setAddProductSaving(false);
    }
  };

  const openEditProduct = (p: ProductRow) => {
    setEditingProductId(p.id);
    setEditProductForm({ product_name: p.product_name ?? '', product_code: p.product_code ?? '', unit: p.unit ?? '' });
    setEditProductError(null);
    setEditProductDialogOpen(true);
  };

  const handleEditProductSave = async () => {
    if (!editProductForm.product_name.trim()) { setEditProductError('Product name is required.'); return; }
    setEditProductSaving(true);
    setEditProductError(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/v1/admin/distributor-products/${editingProductId}`,
        {
          method: 'PUT',
          headers: authHeaders(token),
          body: JSON.stringify({
            product_name: editProductForm.product_name.trim(),
            product_code: editProductForm.product_code || null,
            unit:         editProductForm.unit         || null,
          }),
        },
      );
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setEditProductDialogOpen(false);
      showSnack('Product updated.');
      void fetchProducts();
    } catch (e) {
      setEditProductError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setEditProductSaving(false);
    }
  };

  const handleProductDelete = async () => {
    if (!deleteProductId || !token) return;
    setDeletingProduct(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/v1/admin/distributor-products/${deleteProductId}`,
        { method: 'DELETE', headers: authHeaders(token) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteProductId(null);
      showSnack('Product deleted.');
      void fetchProducts();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeletingProduct(false);
    }
  };

  const handleMatchProducts = async () => {
    if (editingId === null) return;
    setMatching(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/v1/admin/distributor-products/match?distributor_id=${editingId}`,
        { method: 'POST', headers: authHeaders(token) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { matched: number };
      showSnack(`${json.matched} product${json.matched !== 1 ? 's' : ''} matched to chemicals/fertilizers.`);
      void fetchProducts();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Match failed', 'error');
    } finally {
      setMatching(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Distributors</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage chemical and fertilizer distributors
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Distributor
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <CatalogueCountrySwitch value={country} onChange={(c) => { setCountry(c); setPage(0); }} />
            <TextField
              size="small"
              placeholder="Search company, suburb, state…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 320 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'clip' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ tableLayout: 'fixed', minWidth: 1060 }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell sx={{ width: 220 }}>Company</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 180 }}>Street</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 140 }}>Suburb</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 90 }}>Postcode</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 90 }}>State</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 165 }}>Phone</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 80 }}>Web</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell sx={{ fontWeight: 500 }}>{row.company ?? '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.street ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.suburb_name ?? row.suburb ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.postcode ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.state_name ?? row.state ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {row.phone_no ?? '—'}
                      </TableCell>
                      <TableCell align="center">
                        {row.web_link ? (
                          <Tooltip title={row.web_link}>
                            <IconButton
                              size="small"
                              component="a"
                              href={row.web_link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <WebIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => confirmDelete(row.id, row.company ?? String(row.id))}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
            >
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog (Add = simple md, Edit = xl with 3 tabs)          */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth={editingId !== null ? 'xl' : 'md'}
        fullWidth
      >
        <DialogTitle>
          {editingId !== null ? (form.company || 'Edit Distributor') : 'Add Distributor'}
          {form.country_code !== 'AU' && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 600 }}>
              {catalogueCountryName(countries, form.country_code)} catalogue
            </Typography>
          )}
        </DialogTitle>

        {/* Tab bar — edit mode only */}
        {editingId !== null && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs value={activeTab} onChange={(_, v: number) => setActiveTab(v)}>
              <Tab label="Company Details" />
              <Tab label="Stores List" />
              <Tab label="Product List" />
            </Tabs>
          </Box>
        )}

        <DialogContent sx={{ pt: '12px !important' }}>

          {/* ============================================================= */}
          {/* Company Details — shown in Add mode, and Tab 0 in Edit mode    */}
          {/* ============================================================= */}
          {(editingId === null || activeTab === 0) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

              {/* Row 1: Company + Division */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Company Name"
                  size="small"
                  sx={{ flex: 2 }}
                  required
                  autoFocus
                  inputProps={{ maxLength: 255 }}
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                />
                <TextField
                  label="Division"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 100 }}
                  value={form.division}
                  onChange={(e) => setForm((f) => ({ ...f, division: e.target.value }))}
                />
              </Box>

              {/* Row 2: Title + First Name + Last Name */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Title"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 50 }}
                  placeholder="Mr / Ms / Dr"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
                <TextField
                  label="First Name"
                  size="small"
                  sx={{ flex: 2 }}
                  inputProps={{ maxLength: 100 }}
                  value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                />
                <TextField
                  label="Last Name"
                  size="small"
                  sx={{ flex: 2 }}
                  inputProps={{ maxLength: 100 }}
                  value={form.last_name}
                  onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                />
              </Box>

              {/* Row 3: Street */}
              <TextField
                label="Street"
                size="small"
                fullWidth
                inputProps={{ maxLength: 255 }}
                value={form.street}
                onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
              />

              {/* Row 4 (UK): Postcode → Town / County. Town text in suburb, county text in state. */}
              {form.country_code === 'GB' ? (
                <CatalogueUkAddressFields
                  apiUrl={getApiUrl()}
                  token={token}
                  value={{ suburb: form.suburb, state: form.state, postcode: form.postcode }}
                  onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                />
              ) : (
              /* Row 4: State → Suburb → Postcode + Country */
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ flex: 1.5 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    label="State"
                    value={form.state}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, state: e.target.value, suburb: '', postcode: '' }));
                      setSuburbOptions([]);
                    }}
                  >
                    <MenuItem value=""><em>— Select State —</em></MenuItem>
                    {stateOptions.map((s) => (
                      <MenuItem key={s.state_id} value={String(s.state_id)}>{s.state}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  sx={{ flex: 3 }}
                  size="small"
                  options={suburbOptions}
                  loading={loadingSuburbs}
                  disabled={!form.state}
                  filterOptions={(x) => x}
                  getOptionLabel={(o) => o.suburb}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={suburbOptions.find((s) => String(s.id) === form.suburb) ?? null}
                  onInputChange={handleSuburbInputChange}
                  noOptionsText={!form.state ? 'Select State first' : 'Type at least 2 characters…'}
                  onChange={(_e, chosen) => {
                    setSuburbOptions([]);
                    setForm((f) => ({
                      ...f,
                      suburb:   chosen ? String(chosen.id) : '',
                      postcode: chosen?.postcode ?? f.postcode,
                      country:  chosen?.country  ?? f.country,
                    }));
                  }}
                  renderInput={(params) => (
                    // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
                    <TextField
                      {...params}
                      size="small"
                      label="Suburb"
                      placeholder={
                        !form.state ? 'Select State first' :
                        loadingSuburbs ? 'Searching…' : 'Type to search…'
                      }
                    />
                  )}
                />
                <TextField
                  label="Postcode"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 20, readOnly: !!form.suburb }}
                  value={form.postcode}
                  onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value }))}
                />
                <TextField
                  label="Country"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 100 }}
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                />
              </Box>
              )}

              {/* Row 5: Phone + Mobile + Email */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Phone"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 50 }}
                  value={form.phone_no}
                  onChange={(e) => setForm((f) => ({ ...f, phone_no: e.target.value }))}
                />
                <TextField
                  label="Mobile"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 50 }}
                  value={form.mobile_number}
                  onChange={(e) => setForm((f) => ({ ...f, mobile_number: e.target.value }))}
                />
                <TextField
                  label="Email"
                  size="small"
                  sx={{ flex: 2 }}
                  type="email"
                  inputProps={{ maxLength: 255 }}
                  value={form.email_address}
                  onChange={(e) => setForm((f) => ({ ...f, email_address: e.target.value }))}
                />
              </Box>

              {/* Row 6: ABN + Client ID + Invoice Prefix */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="ABN"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 100 }}
                  value={form.abn}
                  onChange={(e) => setForm((f) => ({ ...f, abn: e.target.value }))}
                />
                <TextField
                  label="Client ID"
                  size="small"
                  sx={{ flex: 1 }}
                  type="number"
                  value={form.client_id}
                  onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
                />
                <TextField
                  label="Invoice Prefix"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 50 }}
                  value={form.invoice_prefix}
                  onChange={(e) => setForm((f) => ({ ...f, invoice_prefix: e.target.value }))}
                />
              </Box>

              {/* Row 7: Website */}
              <TextField
                label="Website"
                size="small"
                fullWidth
                placeholder="https://…"
                value={form.web_link}
                onChange={(e) => setForm((f) => ({ ...f, web_link: e.target.value }))}
                InputProps={{
                  endAdornment: form.web_link ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        color="success"
                        title="Open link"
                        onClick={() => { window.open(form.web_link, '_blank')?.focus(); }}
                      >
                        <WebIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />

              {/* Audit Trail — read-only, edit only */}
              {editingId !== null && auditInfo !== null && (
                <Box
                  mt={1}
                  p={2}
                  sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                    Audit Trail
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ============================================================= */}
          {/* Tab 1 — Stores List                                            */}
          {/* ============================================================= */}
          {editingId !== null && activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Manage retail locations for this distributor.
                </Typography>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAddStore}>
                  Add Store
                </Button>
              </Box>
              {storesError && <Alert severity="error" sx={{ mb: 2 }}>{storesError}</Alert>}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <AppTableHead>
                    <TableRow>
                      <AppTableHeadCell>Store Name</AppTableHeadCell>
                      <AppTableHeadCell>Location</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 130 }}>Phone</AppTableHeadCell>
                      <AppTableHeadCell>Email</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 130 }}>Suburb</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 80 }}>Postcode</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 80 }}>State</AppTableHeadCell>
                      <AppTableHeadCell align="right" sx={{ width: 90 }}>Actions</AppTableHeadCell>
                    </TableRow>
                  </AppTableHead>
                  <TableBody>
                    {storesLoading ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
                    ) : stores.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No stores found</TableCell></TableRow>
                    ) : stores.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{s.store_name ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{s.store_location ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{s.phone_number ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{s.email_address ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{s.suburb_name ?? s.suburb ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{s.postcode ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{s.state_name ?? s.state ?? '—'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditStore(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setDeleteStoreId(s.id); setDeleteStoreLabel(s.store_name ?? String(s.id)); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ============================================================= */}
          {/* Tab 2 — Product List                                           */}
          {/* ============================================================= */}
          {editingId !== null && activeTab === 2 && (
            <Box>
              {/* Toolbar */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Manage products offered by this distributor.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {products.filter((p) => p.chemical_id != null || p.fertilizer_id != null).length > 0 && (
                    <Chip
                      label={`${products.filter((p) => p.chemical_id != null || p.fertilizer_id != null).length} linked`}
                      size="small"
                      variant="outlined"
                      color="success"
                    />
                  )}
                  <Tooltip title="Auto-match product names to chemicals and fertilizers">
                    <Button size="small" variant="outlined"
                      startIcon={matching ? <CircularProgress size={14} /> : <MatchIcon />}
                      onClick={() => void handleMatchProducts()} disabled={matching}
                      sx={{ borderColor: '#00bcd4', color: '#00bcd4', '&:hover': { borderColor: '#00acc1', bgcolor: 'rgba(0,188,212,0.08)' } }}
                    >Match Products</Button>
                  </Tooltip>
                  <Tooltip title="Synchronize product codes (legacy)">
                    <Button size="small" variant="outlined" startIcon={<SyncIcon />}
                      onClick={() => showSnack('0 product codes synchronized.')}
                      sx={{ borderColor: '#ffa726', color: '#ffa726', '&:hover': { borderColor: '#fb8c00', bgcolor: 'rgba(255,167,38,0.08)' } }}
                    >Sync Codes</Button>
                  </Tooltip>
                  <Tooltip title="Import products from a CSV file">
                    <Button size="small" variant="outlined" startIcon={<UploadFileIcon />}
                      onClick={() => csvInputRef.current?.click()}
                      sx={{ borderColor: '#42a5f5', color: '#42a5f5', '&:hover': { borderColor: '#1e88e5', bgcolor: 'rgba(66,165,245,0.08)' } }}
                    >Import List</Button>
                  </Tooltip>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const text = ev.target?.result as string;
                        const lines = text.split(/\r?\n/).filter((l) => l.trim());
                        if (lines.length < 2) { showSnack('CSV file is empty or has no data rows.'); return; }
                        const headers = (lines[0] ?? '').split(',').map((h) => h.replace(/^"|"$/g, '').trim());
                        const rows = lines.slice(1).map((line) => {
                          const vals = line.split(',').map((v) => v.replace(/^"|"$/g, '').trim());
                          const row: Record<string, string> = {};
                          headers.forEach((h, i) => { row[h] = vals[i] ?? ''; });
                          return row;
                        });
                        // Auto-map by matching header names
                        const autoMap: Record<string, string> = {};
                        const fields = ['product_name', 'product_code', 'unit'];
                        fields.forEach((f) => {
                          const match = headers.find((h) =>
                            h.toLowerCase().replace(/[^a-z]/g, '') === f.replace(/_/g, ''),
                          );
                          if (match) autoMap[f] = match;
                        });
                        setCsvHeaders(headers);
                        setCsvRows(rows);
                        setCsvMapping(autoMap);
                        setCsvError(null);
                        setCsvDialogOpen(true);
                        e.target.value = '';
                      };
                      reader.readAsText(file);
                    }}
                  />
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => { setAddProductOpen((v) => !v); setAddProductForm(EMPTY_PRODUCT_FORM); setAddProductError(null); }}
                  >Add Product</Button>
                </Box>
              </Box>

              {/* Inline add form */}
              <Collapse in={addProductOpen}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Add New Product</Typography>
                  {addProductError && <Alert severity="error" sx={{ mb: 1 }}>{addProductError}</Alert>}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <TextField label="Product *" size="small" sx={{ flex: 2, minWidth: 180 }}
                      value={addProductForm.product_name}
                      onChange={(e) => setAddProductForm((f) => ({ ...f, product_name: e.target.value }))}
                    />
                    <TextField label="Product Code" size="small" sx={{ flex: 2, minWidth: 140 }}
                      value={addProductForm.product_code}
                      onChange={(e) => setAddProductForm((f) => ({ ...f, product_code: e.target.value }))}
                    />
                    <TextField label="Units" size="small" sx={{ flex: 1, minWidth: 90 }} placeholder="kg, L…"
                      value={addProductForm.unit}
                      onChange={(e) => setAddProductForm((f) => ({ ...f, unit: e.target.value }))}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" size="small" onClick={() => { setAddProductOpen(false); setAddProductError(null); }}>Cancel</Button>
                      <Button variant="contained" size="small" onClick={() => void handleAddProduct()} disabled={addProductSaving}
                        startIcon={addProductSaving ? <CircularProgress size={12} /> : undefined}
                      >Add</Button>
                    </Box>
                  </Box>
                </Paper>
              </Collapse>

              {/* Products table */}
              {productsError && <Alert severity="error" sx={{ mb: 2 }}>{productsError}</Alert>}
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <TableContainer>
                <Table size="small">
                  <AppTableHead>
                    <TableRow>
                      <AppTableHeadCell>Product Name</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 150 }}>Product Code</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 80 }}>Unit</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 160 }}>Linked To</AppTableHeadCell>
                      <AppTableHeadCell align="right" sx={{ width: 90 }}>Actions</AppTableHeadCell>
                    </TableRow>
                  </AppTableHead>
                  <TableBody>
                    {productsLoading ? (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
                    ) : products.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No products found</TableCell></TableRow>
                    ) : products.slice(productsPage * productsRpp, (productsPage + 1) * productsRpp).map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{p.product_name ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{p.product_code ?? '—'}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{p.unit ?? '—'}</TableCell>
                        <TableCell>
                          {p.chemical_id != null ? (
                            <Chip label={p.chemical_name ?? `Chemical #${p.chemical_id}`} size="small" variant="outlined" sx={{ borderColor: 'warning.main', color: 'warning.main' }} />
                          ) : p.fertilizer_id != null ? (
                            <Chip label={p.fertilizer_name ?? `Fertilizer #${p.fertilizer_id}`} size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Unlinked" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditProduct(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setDeleteProductId(p.id); setDeleteProductLabel(p.product_name ?? String(p.id)); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination bar */}
              <Box sx={(theme) => ({
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: theme.palette.mode === 'light'
                  ? alpha(theme.palette.primary.main, 0.09)
                  : darken(theme.palette.background.paper, 0.35),
                px: 2,
                py: 1.5,
              })}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
                >
                  {`Showing ${Math.min(productsPage * productsRpp + 1, products.length)} to ${Math.min((productsPage + 1) * productsRpp, products.length)} of ${products.length} products`}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                    >
                      Show
                    </Typography>
                    <Select
                      size="small"
                      value={productsRpp}
                      onChange={(e) => { setProductsRpp(Number(e.target.value)); setProductsPage(0); }}
                      sx={(theme) => ({
                        color: theme.palette.text.primary,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                        '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                        '& .MuiSelect-select': { py: 0.5, px: 1 },
                      })}
                    >
                      {ROWS_PER_PAGE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {products.length > 0 && (
                    <Typography
                      variant="body2"
                      sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                    >
                      Page {productsPage + 1} of {Math.ceil(products.length / productsRpp)}
                    </Typography>
                  )}

                  {Math.ceil(products.length / productsRpp) > 1 && (
                    <Pagination
                      count={Math.ceil(products.length / productsRpp)}
                      page={productsPage + 1}
                      onChange={(_, v) => setProductsPage(v - 1)}
                      color="primary"
                      size="small"
                      sx={(theme) => ({
                        '& .MuiPaginationItem-root': {
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                          '&:hover': { backgroundColor: theme.palette.action.hover },
                        },
                        '& .Mui-selected': {
                          backgroundColor: `${theme.palette.primary.main} !important`,
                          color: theme.palette.primary.contrastText,
                        },
                        '& .Mui-disabled': { opacity: 0.5 },
                      })}
                    />
                  )}
                </Box>
              </Box>
              </Paper>
            </Box>
          )}

        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {editingId !== null && activeTab !== 0 ? (
            <Button onClick={closeDialog}>Close</Button>
          ) : (
            <>
              <Button onClick={closeDialog}>{editingId !== null ? 'Close' : 'Cancel'}</Button>
              <Button
                variant="contained"
                onClick={() => void handleSave()}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={14} /> : undefined}
              >
                {editingId !== null ? 'Save Changes' : 'Add Distributor'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Store Add / Edit Dialog                                              */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={storeDialogOpen} onClose={() => setStoreDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingStoreId ? 'Edit Store' : 'Add Store'}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {storeFormError && <Alert severity="error" sx={{ mb: 2 }}>{storeFormError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Store Name *" size="small" sx={{ flex: 2 }}
                value={storeForm.store_name} inputProps={{ maxLength: 255 }}
                onChange={(e) => setStoreForm((f) => ({ ...f, store_name: e.target.value }))}
              />
              <TextField label="Location" size="small" sx={{ flex: 2 }}
                value={storeForm.store_location} inputProps={{ maxLength: 255 }}
                onChange={(e) => setStoreForm((f) => ({ ...f, store_location: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Phone" size="small" sx={{ flex: 1 }}
                value={storeForm.phone_number} inputProps={{ maxLength: 50 }}
                onChange={(e) => setStoreForm((f) => ({ ...f, phone_number: e.target.value }))}
              />
              <TextField label="Email" size="small" sx={{ flex: 2 }}
                value={storeForm.email_address} inputProps={{ maxLength: 255 }}
                onChange={(e) => setStoreForm((f) => ({ ...f, email_address: e.target.value }))}
              />
              <TextField label="Website" size="small" sx={{ flex: 2 }}
                value={storeForm.website} inputProps={{ maxLength: 255 }}
                onChange={(e) => setStoreForm((f) => ({ ...f, website: e.target.value }))}
              />
            </Box>
            <TextField label="Full Address" size="small" fullWidth multiline rows={2}
              value={storeForm.full_address}
              onChange={(e) => setStoreForm((f) => ({ ...f, full_address: e.target.value }))}
            />
            {/* UK distributor: Postcode → Town / County (town text in suburb, county text in state). */}
            {form.country_code === 'GB' ? (
              <CatalogueUkAddressFields
                apiUrl={getApiUrl()}
                token={token}
                value={{ suburb: storeForm.suburb, state: storeForm.state, postcode: storeForm.postcode }}
                onChange={(patch) => setStoreForm((f) => ({ ...f, ...patch }))}
              />
            ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>State</InputLabel>
                <Select label="State" value={storeForm.state}
                  onChange={(e) => {
                    setStoreForm((f) => ({ ...f, state: e.target.value as string, suburb: '', postcode: '' }));
                    setStoreSuburbOptions([]);
                  }}
                >
                  <MenuItem value=""><em>— None —</em></MenuItem>
                  {stateOptions.map((s) => (
                    <MenuItem key={s.state_id} value={String(s.state_id)}>{s.state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                sx={{ flex: 2 }} size="small"
                options={storeSuburbOptions}
                getOptionLabel={(o) => o.suburb}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={storeLoadingSuburbs}
                disabled={!storeForm.state}
                filterOptions={(x) => x}
                value={storeSuburbOptions.find((o) => String(o.id) === storeForm.suburb) ?? null}
                onInputChange={handleStoreSuburbInputChange}
                onChange={(_, val) => {
                  if (val) setStoreForm((f) => ({ ...f, suburb: String(val.id), postcode: val.postcode, country: val.country }));
                  else setStoreForm((f) => ({ ...f, suburb: '', postcode: '', country: '' }));
                }}
                renderInput={(params) => (
                  // @ts-expect-error: MUI Autocomplete renderInput params have optional props incompatible with exactOptionalPropertyTypes
                  <TextField {...params} label="Suburb" placeholder="Type to search…"
                    InputProps={{ ...params.InputProps, endAdornment: (<>{storeLoadingSuburbs && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>) }}
                  />
                )}
              />
              <TextField label="Postcode" size="small" sx={{ flex: 1 }}
                value={storeForm.postcode} inputProps={{ maxLength: 10 }}
                onChange={(e) => setStoreForm((f) => ({ ...f, postcode: e.target.value }))}
              />
              <TextField label="Country" size="small" sx={{ flex: 1 }}
                value={storeForm.country} inputProps={{ maxLength: 100 }}
                onChange={(e) => setStoreForm((f) => ({ ...f, country: e.target.value }))}
              />
            </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStoreDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleStoreSave()} disabled={storeSaving}
            startIcon={storeSaving ? <CircularProgress size={14} /> : undefined}
          >
            {editingStoreId ? 'Save Changes' : 'Add Store'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Store */}
      <Dialog open={deleteStoreId !== null} onClose={() => setDeleteStoreId(null)} maxWidth="xs">
        <DialogTitle>Delete Store</DialogTitle>
        <DialogContent><DialogContentText>Delete <strong>{deleteStoreLabel}</strong>? This cannot be undone.</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteStoreId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void handleStoreDelete()} disabled={deletingStore}
            startIcon={deletingStore ? <CircularProgress size={14} /> : undefined}
          >Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={editProductDialogOpen} onClose={() => setEditProductDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {editProductError && <Alert severity="error" sx={{ mb: 2 }}>{editProductError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Product Name *" size="small" fullWidth
              value={editProductForm.product_name}
              onChange={(e) => setEditProductForm((f) => ({ ...f, product_name: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Product Code" size="small" sx={{ flex: 2 }}
                value={editProductForm.product_code}
                onChange={(e) => setEditProductForm((f) => ({ ...f, product_code: e.target.value }))}
              />
              <TextField label="Unit" size="small" sx={{ flex: 1 }} placeholder="kg, L, box"
                value={editProductForm.unit}
                onChange={(e) => setEditProductForm((f) => ({ ...f, unit: e.target.value }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditProductDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleEditProductSave()} disabled={editProductSaving}
            startIcon={editProductSaving ? <CircularProgress size={14} /> : undefined}
          >Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Mapping Dialog */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Product List</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {csvError && <Alert severity="error" sx={{ mb: 2 }}>{csvError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Match your CSV columns to the system fields. {csvRows.length} rows detected.
          </Typography>
          <Table size="small">
            <AppTableHead>
              <TableRow>
                <AppTableHeadCell>System Field</AppTableHeadCell>
                <AppTableHeadCell>Map to CSV Column</AppTableHeadCell>
                <AppTableHeadCell>Preview (Row 1)</AppTableHeadCell>
              </TableRow>
            </AppTableHead>
            <TableBody>
              {([
                { id: 'product_name', label: 'Product Name', required: true },
                { id: 'product_code', label: 'Product Code', required: false },
                { id: 'unit',         label: 'Unit',         required: false },
              ] as const).map((field) => (
                <TableRow key={field.id}>
                  <TableCell>
                    {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={csvMapping[field.id] ?? ''}
                      onChange={(e) => setCsvMapping((m) => ({ ...m, [field.id]: e.target.value }))}
                    >
                      <MenuItem value=""><em>— Ignore —</em></MenuItem>
                      {[...csvHeaders].sort((a, b) => a.localeCompare(b)).map((h) => (
                        <MenuItem key={h} value={h}>{h}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                    {csvRows[0] && csvMapping[field.id] ? (csvRows[0][csvMapping[field.id] as string] || '—') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCsvDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={csvImporting || !csvMapping['product_name']}
            startIcon={csvImporting ? <CircularProgress size={14} /> : undefined}
            onClick={() => {
              if (!csvMapping['product_name'] || editingId === null) return;
              setCsvImporting(true);
              setCsvError(null);
              const products = csvRows
                .map((row) => ({
                  product_name: row[csvMapping['product_name'] as string] ?? '',
                  product_code: csvMapping['product_code'] ? (row[csvMapping['product_code']] ?? '') : '',
                  unit:         csvMapping['unit']         ? (row[csvMapping['unit']]         ?? '') : '',
                }))
                .filter((r) => r.product_name.trim());
              fetch(`${getApiUrl()}/api/v1/admin/distributor-products/bulk-import`, {
                method: 'POST',
                headers: authHeaders(token),
                body: JSON.stringify({ distributor_id: editingId, products }),
              })
                .then(async (res) => {
                  const json = await res.json() as { imported?: number; error?: string };
                  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
                  setCsvDialogOpen(false);
                  showSnack(`${json.imported ?? 0} products imported successfully.`);
                  void fetchProducts();
                })
                .catch((e: unknown) => {
                  setCsvError(e instanceof Error ? e.message : 'Import failed');
                })
                .finally(() => setCsvImporting(false));
            }}
          >
            Import Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Product */}
      <Dialog open={deleteProductId !== null} onClose={() => setDeleteProductId(null)} maxWidth="xs">
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent><DialogContentText>Delete <strong>{deleteProductLabel}</strong>? This cannot be undone.</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteProductId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void handleProductDelete()} disabled={deletingProduct}
            startIcon={deletingProduct ? <CircularProgress size={14} /> : undefined}
          >Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Distributor</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteLabel}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
